import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { ReportSignal } from '../../../types/api';
import { DetailField, DetailFieldGrid } from '../DetailField';
import { DetailSection } from '../DetailSection';
import { FactValueView } from './FactValueView';
import {
    findSignal,
    isImportMetadataSignal,
    REPORT_KEY_SIGNAL_TYPES,
} from './signalTypes';

const KEY_SIGNAL_LABELS: Record<string, string> = {
    'financial.summary': 'signalTypes.financial.summary',
    'subject.financial.loan': 'signalTypes.subject.financial.loan',
    'location.claimed': 'signalTypes.location.claimed',
    'job.outcome': 'signalTypes.job.outcome',
};

export function KeySignalsSummary({ signals }: { signals: ReportSignal[] }) {
    const { t } = useTranslation();

    const keySignals = useMemo(() => {
        const operational = signals.filter((signal) => !isImportMetadataSignal(signal.signalType));
        return REPORT_KEY_SIGNAL_TYPES.map((signalType) => findSignal(operational, signalType)).filter(
            (signal): signal is ReportSignal => Boolean(signal),
        );
    }, [signals]);

    if (keySignals.length === 0) {
        return null;
    }

    return (
        <DetailSection title={t('detail.keySignals')} description={t('detail.keySignalsHint')}>
            <DetailFieldGrid>
                {keySignals.map((signal) => (
                    <DetailField
                        key={signal.id}
                        label={t(
                            KEY_SIGNAL_LABELS[signal.signalType] ??
                                `signalTypes.${signal.signalType}`,
                            { defaultValue: signal.signalType },
                        )}
                        value={
                            <FactValueView
                                recordType={signal.signalType}
                                value={signal.signalValue ?? {}}
                                compact
                            />
                        }
                    />
                ))}
            </DetailFieldGrid>
        </DetailSection>
    );
}
